/**
 * useQzPrint.ts
 *
 * QZ Tray를 통한 실제 출력(라벨/보고서) 및 QZ 미연결 시 브라우저 인쇄 대체를 담당하는 훅.
 * - docType별 프린터·라벨 크기는 usePrinterSettingStore에서 조회.
 * - QZ 연결이 없거나 해당 docType 프린터 미설정 시 HTML은 브라우저 인쇄, 일괄 라벨은 printBulkLabelsAtOnce API로 대체.
 */
import qz from 'qz-tray';
import { useCallback } from 'react';
import config from 'src/config';
import { PrintDocType, usePrinterSettingStore } from 'src/store/usePrinterSettingStore';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { LabelPrintData, printBulkLabelsAtOnce, shouldSkipLabelPrintInDev } from 'src/apis/labelApi';
import { getQzConnected } from 'src/hooks/useQzTray';

/** 출력 데이터 형식: HTML(라벨/보고서), PDF(보고서), raw(바코드 등) */
export type PrintDataType = 'html' | 'pdf' | 'raw';

/** print() 호출 시 넘기는 옵션. docType으로 프린터 선택, labelSize 있으면 라벨용 mm/density 적용 */
export interface QzPrintOptions {
  docType: PrintDocType;
  data: string;
  dataType?: PrintDataType;
  copies?: number;
  labelSize?: { width: number; height: number };
  paperSize?: { width: number; height: number };
}

export const useQzPrint = () => {
  const { getPrinter, getLabelSize } = usePrinterSettingStore();
  const isMediumLabel = useCommonCodeStore(state =>
    state.programList.some(code =>
      code.minorCd === 'LABEEL_PRINT' && code.ref02?.trim().toUpperCase() === 'M'
    )
  );

  /**
   * 공통 출력 함수. docType에 맞는 프린터로 HTML/PDF/raw 전송.
   * QZ 미연결 시: HTML만 새 창 띄워 브라우저 인쇄, PDF는 그대로 실패(호출측에서 blob 다운로드 등 폴백 처리).
   */
  const print = useCallback(async (options: QzPrintOptions): Promise<boolean> => {
    const { docType, data, dataType = 'html', copies = 1, labelSize, paperSize } = options;

    if (!qz.websocket.isActive()) {
      if (dataType === 'html') {
        const win = window.open('', '_blank');
        if (win) {
          win.document.write(data);
          win.document.close();
          win.onload = () => {
            win.print();
            win.close();
          };
        }
      }
      return true;
    }

    const printerName = getPrinter(docType);
    if (!printerName) {
      alert(`[${docType}] 프린터가 설정되지 않았습니다.\n설정 > 프린터 설정 메뉴에서 먼저 설정해주세요.`);
      return false;
    }

    try {
      const configOptions: any = {
        copies,
        colorType: 'color',
        duplex: false,
        scaleContent: true,
        rasterize: dataType === 'pdf',
      };

      if (labelSize) {
        configOptions.units = 'mm';
        configOptions.margins = { top: 0, right: 0, bottom: 0, left: 2 };
        configOptions.orientation = 'portrait';
        configOptions.scaleContent = false;
        configOptions.density = 203;
      }

      if (paperSize) {
        configOptions.units = 'mm';
        configOptions.size = paperSize;
      }

      const printConfig = qz.configs.create(printerName, configOptions);

      let printData: any[];
      if (dataType === 'html') {
        printData = [{ type: 'pixel', format: 'html', flavor: 'plain', data }];
      } else if (dataType === 'pdf') {
        printData = [{ type: 'pixel', format: 'pdf', flavor: 'base64', data }];
      } else {
        printData = [{ type: 'raw', format: 'plain', flavor: 'plain', data }];
      }

      await qz.print(printConfig, printData);
      console.log(` 출력 완료: ${docType} → ${printerName}`);
      return true;

    } catch (err: any) {
      console.error(` 출력 실패: ${docType}`, err);
      alert(`출력 실패: ${err?.message || '알 수 없는 오류'}`);
      return false;
    }
  }, [getPrinter]);

  /**
   * docType이 아닌 프린터 이름을 직접 지정해서 출력. QZ 미연결 시에는 실패 반환(브라우저 대체 없음).
   */
  const printWithPrinterName = useCallback(async (
    printerName: string,
    data: string,
    dataType: PrintDataType = 'html',
    labelSize?: { width: number; height: number },
  ): Promise<boolean> => {
    if (!qz.websocket.isActive()) {
      alert('서버 프린터 설정이 연결되지 않았습니다.');
      return false;
    }

    try {
      const configOptions: any = {
        copies: 1,
        colorType: 'color',
        duplex: false,
        scaleContent: true,
        rasterize: false,
      };

      if (labelSize) {
        configOptions.units = 'mm';
        configOptions.margins = { top: 0, right: 0, bottom: 0, left: 0 };
        configOptions.orientation = 'portrait';
        configOptions.scaleContent = false;
        configOptions.density = 203;
      }

      const printConfig = qz.configs.create(printerName, configOptions);
      const printData: any[] = [{ type: 'pixel', format: 'html', flavor: 'plain', data }];

      await qz.print(printConfig, printData);
      console.log(`직접 출력 완료: ${printerName}`);
      return true;

    } catch (err: any) {
      console.error(' 직접 출력 실패:', err);
      alert(`출력 실패: ${err?.message}`);
      return false;
    }
  }, []);

  /**
   * LOT 번호로 라벨 1건 출력. API에서 HTML 받아와 QZ 또는 브라우저로 출력.
   * QZ 미연결 시: 라벨 URL 새 창으로 열고 load 시 print() 호출.
   */
  const printLabel = useCallback(async (
    lotNo: string,
    docType: PrintDocType = 'label1',
  ): Promise<boolean> => {
    if (shouldSkipLabelPrintInDev()) return true;

    if (!qz.websocket.isActive()) {
      const win = window.open(`${config.apiUrl}/label/print/${lotNo}`, '_blank');
      win?.addEventListener('load', () => { win.print(); });
      return true;
    }

    try {
      const response = await fetch(`${config.apiUrl}/label/print/${lotNo}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      return print({
        docType,
        data: html,
        dataType: 'html',
        labelSize: getLabelSize(docType as 'label1' | 'label2'),
      });
    } catch (err: any) {
      console.error('라벨 HTML 조회 실패:', err);
      alert(`라벨 데이터 조회 실패: ${err?.message}`);
      return false;
    }
  }, [print, getLabelSize]);

  /**
   * 라벨 여러 건 일괄 출력.
   * - QZ 미연결 또는 connect() 실패 이력 또는 해당 docType 프린터 미설정 → printBulkLabelsAtOnce(브라우저 인쇄) 사용.
   * - QZ 연결 + 라벨 프린터 설정됨 → 한 장씩 순서대로 API에서 HTML 받아 print() 호출 (라벨 오프셋 누적 방지).
   * 중간에 QZ 출력 실패 시 해당 회차만 브라우저 인쇄로 대체하고 계속 진행.
   */
  const printBulkLabel = useCallback(async (
    labelDataList: LabelPrintData[],
    docType: PrintDocType = 'label1',
  ): Promise<boolean> => {
    if (shouldSkipLabelPrintInDev()) return true;

    if (!labelDataList || labelDataList.length === 0) {
      alert('출력할 라벨 데이터가 없습니다.');
      return false;
    }

    const useBrowserPrint =
      !getQzConnected() ||
      !qz.websocket.isActive() ||
      ((docType === 'label1' || docType === 'label2') && !getPrinter(docType));

    if (useBrowserPrint) {
      try {
        await printBulkLabelsAtOnce(labelDataList);
        return true;
      } catch (err: any) {
        console.error('일괄 라벨 출력 실패:', err);
        alert(`일괄 출력 실패: ${err?.message}`);
        return false;
      }
    }

    for (let i = 0; i < labelDataList.length; i++) {
      try {
        const response = await fetch(`${config.apiUrl}/label/print/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ labels: [labelDataList[i]] }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();

        const ok = await print({
          docType,
          data: html,
          dataType: 'html',
          labelSize: getLabelSize(docType as 'label1' | 'label2'),
          paperSize: isMediumLabel ? { width: 80, height: 60 } : undefined,
        });
        if (!ok) return false;
        console.log(` 라벨 ${i + 1}/${labelDataList.length} 출력 완료`);
      } catch (err: any) {
        console.error(` 라벨 ${i + 1} 출력 실패, 브라우저 인쇄로 대체:`, err);
        try {
          await printBulkLabelsAtOnce(labelDataList);
          alert('서버 프린터 설정 출력에 실패하여 브라우저 인쇄로 대체했습니다.');
          return true;
        } catch (fallbackErr: any) {
          alert(`라벨 출력 실패: ${err?.message ?? fallbackErr?.message}`);
          return false;
        }
      }
    }
    return true;
  }, [print, getLabelSize, getPrinter, isMediumLabel]);

  /**
   * PDF Blob을 base64로 변환 후 QZ Tray로 출력.
   * API 응답의 response.data(Blob)를 그대로 넘기면 됨. docType은 'report1'/'report2' 등 (설정된 보고서 프린터로 출력).
   */
  const printPdfFromBlob = useCallback(async (
    blob: Blob,
    docType: PrintDocType = 'report1',
  ): Promise<boolean> => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const data = result?.split(',')[1] ?? '';
        resolve(data);
      };
      reader.onerror = () => reject(new Error('PDF base64 변환 실패'));
      reader.readAsDataURL(blob);
    });
    return print({ docType, data: base64, dataType: 'pdf' });
  }, [print]);

  return {
    print,
    printWithPrinterName,
    printLabel,
    printBulkLabel,
    printPdfFromBlob,
  };
};
